// Hooks sobre el registro de generaciones en curso —
// ADR-2026-09-06-use-tandas-panel y ADR-2026-09-06-registro-de-generaciones-en-curso.
//
// El estado de las tandas estaba duplicado en `InterfazV2` (2 creaciones, 6 cierres
// idénticos, 2 fallas, 2 limpiezas). Acá hay UN lugar por el que pasan todos: crear,
// registrar los `execution_id`, cerrar con el preload, fallar, descartar. Y como el
// estado vive en `generacionesStore` (fuera de React), la promesa de una generación lo
// actualiza aunque el componente que la disparó ya se haya desmontado.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { supabase } from '../../lib/supabase';
import { CACHE_KEYS, getCached } from '../../utils/sessionCache';
import { empresaRecordada, recordarEmpresa } from './empresaDeLaSesion';
import {
  preloadGeneratedImages,
  type PreloadedImage,
} from '../../utils/preloadGeneratedImages';
import { preloadVideosDeTanda } from '../../utils/preloadVideosDeTanda';
import type { TandaPanel } from './components/PanelTandaCreaciones';
import { pollGeneratedByExecutionId } from './pollGeneratedByExecutionId';
import {
  edadRestanteMs,
  filtrarPorEmpresa,
  generacionesStore,
  pantallasDeCarga,
  paraReanudar,
  puedeAgregar,
  type GeneracionEnCurso,
} from './generacionesEnCurso';

const store = generacionesStore;

/** Lo que hace falta para crear una tanda; el resto (empresa, reloj, estado) lo pone el hook. */
export interface NuevaTanda {
  total: number;
  ratio: number;
  producto?: string;
  modo?: string;
  creditos?: number;
  esVideo?: boolean;
  executionIds?: string[];
  /** Estimación de cuánto tarda (ms), para la barra de los recuadros. */
  duracionEstimadaMs?: number;
}

export type SetTandasPanel = (
  updater: TandaPanel[] | ((prev: TandaPanel[]) => TandaPanel[]),
) => void;

export interface TandasPanelApi {
  /**
   * Las tandas de ESTA empresa, de la más nueva a la más vieja — las de TODOS los modos.
   *
   * Hubo un `tandasDelModo` que filtraba por el modo abierto
   * (ADR-2026-09-04-la-tanda-no-se-muda-de-modo): una tanda de Fotos de producto no se
   * veía desde Modelo, y el usuario que cambiaba de modo con algo generando la daba por
   * perdida. Ahora la columna dibuja todas y rotula las de otro modo
   * (ADR-2026-09-06-las-tandas-se-ven-en-todos-los-modos).
   */
  tandasPanel: GeneracionEnCurso[];
  /**
   * Con la firma de `useState`, para las ramas que todavía la usen: aplica el updater a
   * las tandas de esta empresa y conserva las de las demás.
   */
  setTandasPanel: SetTandasPanel;
  /** Qué muestra la columna derecha: las tandas o la configuración/muestra. */
  columnaMuestraTandas: boolean;
  setColumnaMuestraTandas: (v: boolean) => void;
  /**
   * La columna TIENE resultados que mostrar — lo que hay que preguntar antes de caer a la
   * portada del modo, en vez de `tandasPanel.length > 0`
   * (ADR-2026-09-08-la-portada-no-parpadea-mientras-se-resuelve-la-empresa).
   *
   * Es `tandasPanel.length > 0` con una salvedad: mientras la empresa no está resuelta
   * —cada pantalla la pide con su propia consulta al montar— el filtro por empresa
   * devuelve vacío, y preguntar por la lista filtrada da "no hay nada" durante ese ida y
   * vuelta. Con algo en el registro y la empresa todavía sin llegar, la respuesta es que
   * SÍ hay: el registro es de esta pestaña, así que es de esta sesión.
   */
  hayResultados: boolean;
  /** Cambia cuando las grillas tienen que volver a pedir la lista. */
  recargarToken: number;
  pedirRecarga: () => void;
  /** Crea la tanda ARRIBA y muestra la columna. `null` sin empresa resuelta. */
  crearTanda: (p: NuevaTanda) => string | null;
  /**
   * Los `execution_id` reales de la tanda, para reanudarla tras una recarga. `total`
   * corrige la previsión con la cuenta REAL de imágenes esperadas cuando el que
   * genera la sabe recién ahí (el fan-out de Modelo).
   */
  registrarExecutionIds: (
    id: string,
    executionIds: string[],
    total?: number,
  ) => void;
  /** Cierra con lo que el preload resolvió; vacío → pide recarga a las grillas. */
  cerrarTanda: (id: string, preload: PreloadedImage[], opts?: { esVideo?: boolean }) => void;
  /** La corrida falló: sus recuadros se van; las otras siguen. */
  fallarTanda: (id: string) => void;
  /** Tope de tandas en curso (ADR-2026-09-06-tope-de-tres-tandas-en-curso). */
  puedeCrear: () => boolean;
}

export function useTandasPanel(
  companyId: string | null | undefined,
  modo: string | undefined,
): TandasPanelApi {
  const lista = useSyncExternalStore(store.subscribe, store.get, store.get);
  const recargarToken = useSyncExternalStore(
    store.subscribe,
    store.recargaVersion,
    store.recargaVersion,
  );
  const tandasPanel = useMemo(() => filtrarPorEmpresa(lista, companyId), [lista, companyId]);
  /**
   * ¿Hay resultados que mostrar? — ADR-2026-09-08-la-portada-no-parpadea-mientras-se-resuelve-la-empresa.
   *
   * `filtrarPorEmpresa` devuelve VACÍO mientras `companyId` es null, y cada pantalla lo
   * resuelve con su propia consulta al montar (`auth.getUser()` + `profiles`): son unos
   * cientos de ms en los que la columna concluía "no hay nada" y dibujaba la portada del
   * modo, para reemplazarla por los resultados en cuanto llegaba el id. Ese es el
   * parpadeo al cambiar de modo.
   *
   * Con la empresa sin resolver y ALGO en el registro, se asume que hay: el registro vive
   * en la pestaña, así que lo que hay es de esta sesión. Si al resolverse resulta que era
   * de otra empresa, `tandasPanel` queda vacío y la columna cede a la portada — el caso
   * raro (dos cuentas en la misma pestaña sin recargar) paga el parpadeo que antes pagaban
   * todos.
   */
  const hayResultados = tandasPanel.length > 0 || (!companyId && lista.length > 0);
  // Al montar con tandas en el registro —corriendo o ya terminadas— la columna arranca
  // en ellas: es lo que el usuario dejó, esperando o listo
  // (ADR-2026-09-08-los-resultados-se-quedan-toda-la-sesion). Corriendo O terminadas y
  // no sólo corriendo: volver de `/animar-imagen` a Automática desmonta esta pantalla, y
  // con la tanda ya cerrada la columna volvía a la muestra del modo — las imágenes recién
  // generadas desaparecían de la vista.
  const [columnaMuestraTandas, setColumnaMuestraTandas] = useState<boolean>(
    () => hayResultados,
  );
  // Sin resultados no hay nada que mostrar: la columna vuelve a la configuración. Pasa
  // cuando la única tanda que había se cayó con un error. Va como efecto y no adentro del
  // updater del store, que puede correr dos veces en StrictMode.
  useEffect(() => {
    if (!hayResultados) setColumnaMuestraTandas(false);
  }, [hayResultados]);
  /**
   * Con una tanda del modo CORRIENDO, la columna las muestra: cerrar el sheet ya no las
   * pierde, así que al reabrir el modo —o al volver de una recarga— tienen que volver a
   * verse (ADR-2026-09-06-registro-de-generaciones-en-curso).
   *
   * Depende de que HAYA alguna en curso y no sólo de `modo`: la empresa se resuelve
   * async, así que tras una recarga `modo` llega antes que `companyId` y en ese momento
   * la lista todavía está vacía — el efecto corría en falso y los recuadros quedaban
   * existiendo sin dibujarse.
   *
   * `apagadaPorElUsuario` es lo que impide que "Cambiar modelo y pose" se deshaga solo:
   * ese botón apaga la columna con la tanda todavía corriendo
   * (ADR-2026-08-20-cambiar-modelo-y-pose-destacado), y sin la marca este efecto la
   * volvería a encender en el render siguiente.
   */
  const apagadaPorElUsuario = useRef(false);
  // De CUALQUIER modo y en CUALQUIER estado: entrar a Modelo con una tanda de Fotos de
  // producto corriendo prende la columna en tandas, que es donde se la ve seguir
  // (ADR-2026-09-06-las-tandas-se-ven-en-todos-los-modos), y entrar con una ya terminada
  // la prende igual, que es donde se ve lo que salió
  // (ADR-2026-09-08-los-resultados-se-quedan-toda-la-sesion).
  useEffect(() => {
    apagadaPorElUsuario.current = false;
  }, [modo]);
  useEffect(() => {
    if (hayResultados && !apagadaPorElUsuario.current) {
      setColumnaMuestraTandas(true);
    }
  }, [modo, hayResultados]);
  const cambiarColumna = useCallback((v: boolean) => {
    if (!v) apagadaPorElUsuario.current = true;
    setColumnaMuestraTandas(v);
  }, []);

  const setTandasPanel = useCallback<SetTandasPanel>(
    (updater) => {
      store.set((prev) => {
        const mias = filtrarPorEmpresa(prev, companyId);
        const otras = companyId ? prev.filter((g) => g.companyId !== companyId) : prev;
        const nuevas = typeof updater === 'function' ? updater(mias) : updater;
        const normalizadas: GeneracionEnCurso[] = nuevas.map((t) => ({
          companyId: companyId ?? '',
          executionIds: [],
          esVideo: false,
          ...t,
        }));
        return [...normalizadas, ...otras];
      });
    },
    [companyId],
  );

  const crearTanda = useCallback(
    (p: NuevaTanda): string | null => {
      if (!companyId) return null;
      const id = store.agregar({
        companyId,
        total: p.total,
        ratio: p.ratio,
        producto: p.producto,
        modo: p.modo,
        creditos: p.creditos,
        imagenes: [],
        terminada: false,
        // Se sella acá, en el toque de Generar, y no en el panel: el panel se desmonta
        // al cambiar de modo y volvería a arrancar todos los relojes en cero.
        creadaEn: Date.now(),
        executionIds: p.executionIds ?? [],
        esVideo: p.esVideo ?? false,
        ...(typeof p.duracionEstimadaMs === 'number'
          ? { duracionEstimadaMs: p.duracionEstimadaMs }
          : {}),
      });
      apagadaPorElUsuario.current = false;
      setColumnaMuestraTandas(true);
      return id;
    },
    [companyId],
  );

  const registrarExecutionIds = useCallback(
    (id: string, executionIds: string[], total?: number) => {
      store.actualizar(id, {
        executionIds,
        // El `total` con el que se creó la tanda es la previsión del pill; el del poll
        // sale de `resolveTandaModelo`, que es la cuenta que de verdad se pidió. Si
        // difieren, mandan los recuadros —y el `expectedCount` del reanudador— sobre
        // la real (ADR-2026-09-06-registro-de-generaciones-en-curso).
        ...(typeof total === 'number' && total > 0 ? { total } : {}),
      });
    },
    [],
  );

  const cerrarTanda = useCallback(
    (id: string, preload: PreloadedImage[], opts?: { esVideo?: boolean }) => {
      store.cerrar(
        id,
        preload.map((p) => ({
          id: String(p.id),
          src: p.optSignedUrl || p.signedUrl,
          ...(opts?.esVideo ? { esVideo: true } : {}),
        })),
      );
      // Sin nada que dibujar (el preload no alcanzó a resolver las URLs), las filas ya
      // están en la base: la grilla las trae
      // (ADR-2026-09-04-la-generacion-del-modo-nunca-cambia-de-pantalla).
      if (preload.length === 0) store.pedirRecarga();
    },
    [],
  );

  const fallarTanda = useCallback((id: string) => {
    store.quitar(id);
  }, []);

  const puedeCrear = useCallback(
    () => puedeAgregar(store.get(), companyId),
    [companyId],
  );

  return {
    tandasPanel,
    setTandasPanel,
    hayResultados,
    columnaMuestraTandas,
    setColumnaMuestraTandas: cambiarColumna,
    recargarToken,
    pedirRecarga: store.pedirRecarga,
    crearTanda,
    registrarExecutionIds,
    cerrarTanda,
    fallarTanda,
    puedeCrear,
  };
}

/** Las generaciones (en curso y terminadas) de una empresa — para las cards
 *  "Generando…" de Creaciones. */
export function useGeneracionesDeEmpresa(
  companyId: string | null | undefined,
): GeneracionEnCurso[] {
  const lista = useSyncExternalStore(store.subscribe, store.get, store.get);
  return useMemo(() => filtrarPorEmpresa(lista, companyId), [lista, companyId]);
}

/**
 * Ids que YA tienen un poll de reanudación corriendo en esta pestaña. Es de módulo y no
 * del efecto para que dos montajes (StrictMode, dos shells) no polleen dos veces la
 * misma tanda.
 */
const enPoll = new Set<string>();

/**
 * Reanuda las tandas rehidratadas (las que quedaron corriendo cuando se recargó la
 * página) polleando por sus `execution_id`. Al terminar, quita la entrada y pide
 * recarga: las imágenes entran por las grillas, no se persisten URLs. Nunca navega
 * (ADR-2026-09-04-la-generacion-del-modo-nunca-cambia-de-pantalla).
 */
export function useReanudarGeneraciones(companyId: string | null | undefined): void {
  useEffect(() => {
    if (!companyId) return;
    const ac = new AbortController();
    store.purgarViejas();
    const ahora = Date.now();
    const pendientes = paraReanudar(
      filtrarPorEmpresa(store.get(), companyId),
      store.idsVivos,
      ahora,
    );
    const mios: string[] = [];
    for (const g of pendientes) {
      if (enPoll.has(g.id)) continue;
      enPoll.add(g.id);
      mios.push(g.id);
      pollGeneratedByExecutionId({
        executionId: g.executionIds,
        companyId,
        expectedCount: g.total,
        timeoutMs: edadRestanteMs(g, ahora),
        signal: ac.signal,
      })
        .then(async (rows) => {
          if (ac.signal.aborted) return;
          const ok = rows.filter((r) => !r.has_error);
          if (ok.length === 0) {
            // Ni una imagen: la tanda se va y se pide recarga igual, por si algo
            // entró por otro camino. El toast no corresponde — el error ya pasó en
            // la sesión anterior, antes de la recarga.
            store.quitar(g.id);
            store.pedirRecarga();
            return;
          }
          // Se CIERRA, no se descarta: así el resultado aterriza en la columna del
          // modo como cualquier otra tanda, en vez de depender de que la grilla lo
          // traiga de vuelta (ADR-2026-09-06-registro-de-generaciones-en-curso).
          let preload: PreloadedImage[] = [];
          try {
            const ids = ok.map((r) => r.id);
            preload = g.esVideo
              ? await preloadVideosDeTanda(ids, companyId)
              : await preloadGeneratedImages(ids, companyId);
          } catch {
            /* sin preload cierra vacía y la grilla las trae */
          }
          if (ac.signal.aborted) return;
          store.cerrar(
            g.id,
            preload.map((p) => ({
              id: String(p.id),
              src: p.optSignedUrl || p.signedUrl,
              ...(g.esVideo ? { esVideo: true } : {}),
            })),
          );
          store.pedirRecarga();
        })
        .catch(() => {
          /* el poll no rechaza; por las dudas */
        })
        .finally(() => {
          // Si este montaje ya se limpió, el id puede ser del montaje siguiente: no se
          // le pisa la marca.
          if (!ac.signal.aborted) enPoll.delete(g.id);
        });
    }
    return () => {
      ac.abort();
      for (const id of mios) enPoll.delete(id);
    };
  }, [companyId]);
}

interface PerfilCacheado {
  id_company?: string | null;
}

/**
 * Componente headless que va en el shell (`ShellApp`): resuelve la empresa y reanuda
 * las generaciones pendientes desde cualquier ruta — también desde Creaciones, que es
 * donde el usuario va a mirar si "generé y se me fue".
 */
/**
 * La empresa del usuario logueado, para lo que corre en el SHELL y no adentro de una
 * pantalla que ya la tenga a mano: el reanudador y el aviso de "generando de fondo".
 * Primero el perfil cacheado (instantáneo); si no está, se pide.
 */
export function useCompanyIdDelUsuario(): string | null {
  const [companyId, setCompanyId] = useState<string | null>(
    // La recordada primero: dura toda la pestaña, mientras que el perfil cacheado vence a
    // los 5 minutos (ADR-2026-09-08-la-portada-no-parpadea-mientras-se-resuelve-la-empresa).
    () =>
      empresaRecordada() ??
      getCached<PerfilCacheado>(CACHE_KEYS.USER_PROFILE)?.id_company ??
      null,
  );
  useEffect(() => {
    if (companyId) return;
    let cancelado = false;
    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user || cancelado) return;
        const { data } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .maybeSingle();
        const id = (data as { id_company?: string | null } | null)?.id_company ?? null;
        // Este hook corre en el SHELL, o sea en cualquier ruta: es el que deja la empresa
        // anotada para que los modos con ruta propia no tengan que esperar la suya.
        recordarEmpresa(id, user.id);
        if (!cancelado && id) setCompanyId(id);
      } catch {
        /* sin empresa no hay nada que hacer */
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [companyId]);
  return companyId;
}

export function ReanudadorDeGeneraciones(): null {
  const companyId = useCompanyIdDelUsuario();
  useReanudarGeneraciones(companyId);
  return null;
}

/**
 * "Esta pantalla está mostrando la carga": la columna de resultados de un modo y la
 * pantalla de carga full-screen se anotan mientras están montadas, y el aviso flotante
 * de "generando de fondo" se calla mientras haya alguna
 * (ADR-2026-09-06-el-aviso-de-generando-de-fondo).
 */
export function useRegistrarPantallaDeCarga(activo = true): void {
  useEffect(() => {
    if (!activo) return;
    return pantallasDeCarga.registrar();
  }, [activo]);
}

/** Cuántas pantallas están mostrando la carga ahora. */
export function usePantallasDeCarga(): number {
  return useSyncExternalStore(
    pantallasDeCarga.subscribe,
    pantallasDeCarga.cantidad,
    pantallasDeCarga.cantidad,
  );
}
